'use client';

import {
  type AriaAttributes,
  cloneElement,
  type ComponentType,
  type FocusEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactElement,
  type ReactNode,
  useEffect,
  useState,
} from 'react';
import { JS_VIEW_LOGIN_POPUP_EVENT_NAME } from '@/common/constant';
import { usePosthog } from '@/hooks/use-posthog';
import {
  getAuthModalSourceScreen,
  getCurrentWebsiteTrackingScreen,
} from '@/services/opportunity-tracking/source-screen';
import type { LoginModalProps } from '.';

type DeferredTriggerProps = {
  'aria-controls'?: string;
  'aria-expanded'?: boolean;
  'aria-haspopup'?: AriaAttributes['aria-haspopup'];
  'aria-busy'?: boolean;
  disabled?: boolean;
  onClick?: (event: MouseEvent<HTMLElement>) => void | Promise<void>;
  onFocus?: (event: FocusEvent<HTMLElement>) => void;
  onPointerDown?: (event: PointerEvent<HTMLElement>) => void;
  onPointerEnter?: (event: PointerEvent<HTMLElement>) => void;
};

type DeferredAuthModalProps = Pick<
  LoginModalProps,
  'authenticatedContent' | 'closeRedirectTo' | 'redirectTo' | 'type'
> & {
  triggerButton: ReactElement<DeferredTriggerProps>;
};

let authModalPromise: Promise<ComponentType<LoginModalProps>> | null = null;

function loadAuthModal() {
  if (!authModalPromise) {
    const request = import('.').then(module => module.default);

    authModalPromise = request;
    void request.catch(() => {
      if (authModalPromise === request) authModalPromise = null;
    });
  }

  return authModalPromise;
}

export function DeferredAuthModal({
  authenticatedContent,
  closeRedirectTo,
  redirectTo,
  triggerButton,
  type,
}: DeferredAuthModalProps) {
  const [AuthModal, setAuthModal] =
    useState<ComponentType<LoginModalProps> | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const { track } = usePosthog();

  useEffect(() => {
    if (!shouldLoad || AuthModal) return;

    let isCancelled = false;

    void loadAuthModal()
      .then(LoadedAuthModal => {
        if (isCancelled) return;

        setAuthModal(() => LoadedAuthModal);
        setIsOpen(true);
        setShouldLoad(false);
      })
      .catch(error => {
        console.error('[AuthModal] Không thể tải hộp thoại đăng nhập:', error);
        if (!isCancelled) setShouldLoad(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [AuthModal, shouldLoad]);

  const preloadAuthModal = () => {
    void loadAuthModal();
  };

  const handleTriggerClick = (event: MouseEvent<HTMLElement>) => {
    try {
      const action = triggerButton.props.onClick?.(event);

      void Promise.resolve(action).catch(error => {
        console.error(
          '[AuthModal] Không thể hoàn tất hành động trước đăng nhập:',
          error
        );
      });
    } catch (error) {
      console.error(
        '[AuthModal] Không thể hoàn tất hành động trước đăng nhập:',
        error
      );
    }

    if (event.defaultPrevented) return;

    const sourceScreen =
      getAuthModalSourceScreen(type) ?? getCurrentWebsiteTrackingScreen();

    track(
      JS_VIEW_LOGIN_POPUP_EVENT_NAME,
      sourceScreen ? { source_screen: sourceScreen } : {}
    );

    if (AuthModal) {
      setIsOpen(true);

      return;
    }

    setShouldLoad(true);
  };

  if (AuthModal && isOpen) {
    return (
      <AuthModal
        authenticatedContent={authenticatedContent}
        closeRedirectTo={closeRedirectTo}
        defaultOpen
        isLoggedIn={false}
        onClose={() => setIsOpen(false)}
        redirectTo={redirectTo}
        triggerButton={triggerButton as ReactNode}
        type={type}
      />
    );
  }

  return cloneElement(triggerButton, {
    'aria-controls': 'auth-dialog',
    'aria-expanded': false,
    'aria-haspopup': 'dialog',
    'aria-busy': shouldLoad || undefined,
    disabled: triggerButton.props.disabled || shouldLoad,
    onClick: handleTriggerClick,
    onFocus: event => {
      triggerButton.props.onFocus?.(event);
      preloadAuthModal();
    },
    onPointerDown: event => {
      triggerButton.props.onPointerDown?.(event);
      preloadAuthModal();
    },
    onPointerEnter: event => {
      triggerButton.props.onPointerEnter?.(event);
      preloadAuthModal();
    },
  });
}
